import React, { useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import { useTeams } from "../../../contexts/Teams";
import { ReactComponent as AddIcon } from "../../../assets/Icons/add.svg";
import { useCommon } from "../../../contexts/common";
import Grid from "@mui/material/Grid";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { additionalEducationSchema } from "../../../validations/registerValidation";
import EducationForm from "../../Onboarding/Education/EducationForm";
import CustomButton from "../../CustomButton";
import "./style.css";
import { trackClickEvent } from "../../../mixpanel";

const Education = ({ role }) => {
  const { next, invitationState, setInvitationState, updateInvitationState } =
    useTeams();
  const { fetchDegreesList, degreesList } = useCommon();
  const [selectedDegrees, setSelectedDegree] = useState([]);

  const initialState = {
    professional_degree_id: null,
    degree_year: "",
    college_name: "",
  };

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
    trigger,
    setValue,
  } = useForm({
    resolver: zodResolver(additionalEducationSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      degrees: invitationState?.degrees || [initialState],
    },
  });

  useEffect(() => {
    fetchDegreesList();
    if (!invitationState?.degrees?.length) {
      updateInvitationState({ degrees: [initialState] });
    }
  }, []);

  useEffect(() => {
    reset({
      degrees: invitationState?.degrees || [initialState],
    });
  }, [invitationState]);

  useEffect(() => {
    const selectedDegreeIds = invitationState?.degrees?.map(
      (degree) => degree.professional_degree_id
    );
    setSelectedDegree(selectedDegreeIds);
  }, [invitationState?.degrees, degreesList]);

  const handleChange = async (selected, index) => {
    const { name, value } = selected.target;
    const slicedName = name.split(".");
    const updatedDegrees = invitationState.degrees.map((degree, i) =>
      i === index ? { ...degree, [slicedName[2]]: value } : degree
    );

    updateInvitationState({
      degrees: updatedDegrees,
    });
    setValue(`degrees.${index}.${slicedName[2]}`, value);
    await trigger(`degrees.${index}.${slicedName[2]}`);
  };

  const handleDeleteDegree = (indexToDelete) => {
    const updatedDegrees = invitationState.degrees.filter(
      (_, index) => index !== indexToDelete
    );
    updateInvitationState({
      degrees: updatedDegrees,
    });
  };

  const onSubmit = () => {
    if (isValid) {
      trackClickEvent("Invitation - Education", {
        page: "Invitation Onboarding",
        button: "Continue",
        role,
      });
      next();
    } else {
      trigger([
        "professional_degree_id",
        "degree_year",
        "practice_year",
        "college_name",
      ]);
    }
  };

  const addNew = async () => {
    const isTriggered = await trigger();
    if (isValid && isTriggered) {
      updateInvitationState({
        degrees: [...(invitationState.degrees || []), { ...initialState }],
      });
    } else {
      await trigger();
    }
  };

  return (
    <Container maxWidth="md">
      <Box>
        <div className="education">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography variant="h4" mb={1} fontWeight={600}>
                Education
              </Typography>
              <Typography variant="body1" mb={3}>
                Where did you go to school, and what degree did you earn?
              </Typography>
            </Box>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              {invitationState?.degrees?.map((degree, index) => (
                <EducationForm
                  key={index}
                  degreesList={degreesList}
                  selectedDegrees={selectedDegrees}
                  onChange={(selected) => handleChange(selected, index)}
                  onDelete={() => handleDeleteDegree(index)}
                  showDeleteButton={invitationState.degrees.length > 1}
                  control={control}
                  degree={degree}
                  errors={errors}
                  index={index}
                />
              ))}
            </Grid>
          </Grid>
          <Button
            sx={{
              color: "primary.main",
              p: 1.125,
              minWidth: 0,
              mt: 1.5,
              mb: 0.5,
            }}
            onClick={addNew}
            variant="outlined"
          >
            <AddIcon width={18} height={18} />
            Add New Education
          </Button>
        </div>
      </Box>
      <div className="stickyButton">
        <Box textAlign="center">
          <CustomButton
            className="btnContinue"
            variant="contained"
            sx={{ minWidth: 212 }}
            onClick={(e) => handleSubmit(onSubmit)(e)}
            page="Register"
            name="Continue"
            eventName="U-Step 5 - Education"
          >
            Continue
          </CustomButton>
        </Box>
      </div>
    </Container>
  );
};

export default Education;
